// EventsMarketingPage.jsx — Hauptseite events.tda-intl.org
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import './EventsMarketingPage.css';

// Apple-artiges Scroll-Reveal (wie HOF-Übersichtsseite): Elemente mit .reveal
// werden beim Eintritt in den Viewport sanft/langsam eingeblendet. reduced-motion-fest.
function useScrollReveal(deps = []) {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll('.reveal:not(.in)'));
    if (!els.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      els.forEach(el => el.classList.add('in'));
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); obs.unobserve(e.target); } });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

function formatDate(dateStr, dateText) {
  if (dateText) return dateText;
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function DeadlineBadge({ deadline }) {
  const diff = new Date(deadline) - Date.now();
  if (diff <= 0) return <span className="emp-badge emp-badge--closed">Anmeldung geschlossen</span>;
  const days = Math.floor(diff / 86400000);
  if (days > 14) return <span className="emp-badge emp-badge--open">Anmeldung offen</span>;
  if (days > 0)  return <span className="emp-badge emp-badge--soon">Noch {days} Tag{days !== 1 ? 'e' : ''}</span>;
  const hours = Math.floor(diff / 3600000);
  return <span className="emp-badge emp-badge--urgent">Noch {hours} Std.</span>;
}

function TurnierCard({ turnier, onClick }) {
  const kategorienList = turnier.kategorien
    ? turnier.kategorien.split(',').filter(Boolean)
    : [];

  const hasImage = turnier.banner_url || turnier.plakat_url;

  return (
    <div className="emp-turnier-card" onClick={onClick} role="button" tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}>
      <div className="emp-card-poster-wrap">
        {turnier.banner_url ? (
          <img src={turnier.banner_url} alt={turnier.name} className="emp-card-poster" />
        ) : turnier.plakat_url ? (
          <img src={turnier.plakat_url} alt={turnier.name} className="emp-card-poster emp-card-poster--portrait" />
        ) : (
          <div className="emp-card-poster-placeholder">
            <div className="emp-placeholder-icon">🏆</div>
            <div className="emp-placeholder-label">TDA Turnier</div>
          </div>
        )}
        {hasImage && <div className="emp-card-img-overlay" />}
        {turnier.anmeldeschluss && (
          <div className="emp-card-badge-wrap">
            <DeadlineBadge deadline={turnier.anmeldeschluss} />
          </div>
        )}
      </div>

      <div className="emp-card-body">
        <h3 className="emp-card-name">{turnier.name}</h3>

        <div className="emp-card-meta">
          <div className="emp-card-meta-row">
            <span className="emp-meta-icon">📅</span>
            <span>{formatDate(turnier.datum, turnier.datum_text)}</span>
          </div>
          <div className="emp-card-meta-row">
            <span className="emp-meta-icon">📍</span>
            <span>{turnier.ort}</span>
          </div>
        </div>

        {kategorienList.length > 0 && (
          <div className="emp-card-kat-wrap">
            {kategorienList.slice(0, 3).map((k, i) => (
              <span key={i} className="emp-card-kat">{k}</span>
            ))}
            {kategorienList.length > 3 && (
              <span className="emp-card-kat emp-card-kat--more">+{kategorienList.length - 3}</span>
            )}
          </div>
        )}

        <div className="emp-card-footer">
          <span className="emp-card-detail-link">
            Details ansehen
            <svg className="emp-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 7h10M7 2l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        </div>
      </div>
    </div>
  );
}

// Karte für Camps / Lehrgänge (events-Tabelle, öffentliche Seite unter /veranstaltung/:id)
function CampCard({ camp, onClick }) {
  const zeitraum = camp.end_datum && camp.end_datum !== camp.datum
    ? `${formatDate(camp.datum)} – ${formatDate(camp.end_datum)}`
    : formatDate(camp.datum);

  return (
    <div className="emp-turnier-card" onClick={onClick} role="button" tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}>
      <div className="emp-card-poster-wrap">
        {camp.bild ? (
          <img src={camp.bild} alt={camp.name} className="emp-card-poster" />
        ) : (
          <div className="emp-card-poster-placeholder">
            <div className="emp-placeholder-icon">合宿</div>
            <div className="emp-placeholder-label">TDA {camp.typ || 'Camp'}</div>
          </div>
        )}
        {camp.bild && <div className="emp-card-img-overlay" />}
        {camp.anmeldeschluss && (
          <div className="emp-card-badge-wrap">
            <DeadlineBadge deadline={camp.anmeldeschluss} />
          </div>
        )}
      </div>

      <div className="emp-card-body">
        <h3 className="emp-card-name">{camp.name}</h3>

        <div className="emp-card-meta">
          <div className="emp-card-meta-row">
            <span className="emp-meta-icon">📅</span>
            <span>{zeitraum}</span>
          </div>
          {camp.ort && (
            <div className="emp-card-meta-row">
              <span className="emp-meta-icon">📍</span>
              <span>{camp.ort}</span>
            </div>
          )}
        </div>

        <div className="emp-card-kat-wrap">
          {camp.typ && <span className="emp-card-kat">{camp.typ}</span>}
          {camp.trainer_referent && <span className="emp-card-kat">{camp.trainer_referent}</span>}
        </div>

        <div className="emp-card-footer">
          <span className="emp-card-detail-link">
            Anmelden & Details
            <svg className="emp-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 7h10M7 2l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        </div>
      </div>
    </div>
  );
}

export default function EventsMarketingPage() {
  const navigate = useNavigate();
  const [turniere, setTurniere]   = useState([]);
  const [loadingT, setLoadingT]   = useState(true);
  const [camps, setCamps]         = useState([]);
  const [nlEmail, setNlEmail]     = useState('');
  const [nlName, setNlName]       = useState('');
  const [nlMsg, setNlMsg]         = useState('');
  const [nlLoading, setNlLoading] = useState(false);

  const handleNlSubmit = async (e) => {
    e.preventDefault();
    setNlLoading(true); setNlMsg('');
    try {
      const r = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: nlEmail, name: nlName }),
      });
      const d = await r.json();
      setNlMsg(d.success ? '✅ ' + d.message : '❌ ' + d.error);
      if (d.success) { setNlEmail(''); setNlName(''); }
    } catch { setNlMsg('❌ Verbindungsfehler'); }
    finally { setNlLoading(false); }
  };

  useEffect(() => {
    fetch('/api/turniere/public')
      .then(r => r.json())
      .then(d => { if (d.success) setTurniere(d.data); })
      .catch(() => {})
      .finally(() => setLoadingT(false));

    // Camps / Lehrgänge (events-Tabelle) — z. B. Sommercamp, Trainingscamps
    fetch('/api/events/public')
      .then(r => r.json())
      .then(d => { if (d.success) setCamps(d.data || []); })
      .catch(() => {});
  }, []);

  // Turniere + Camps zu EINER chronologischen Liste zusammenführen.
  // Undatierte (nur datum_text, z. B. „Herbst 2026") ans Ende — wie im Backend.
  const sortTs = (d) => {
    const t = d ? new Date(d).getTime() : NaN;
    return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
  };
  const veranstaltungen = [
    ...turniere.map(t => ({ kind: 'turnier', ts: sortTs(t.datum), key: `t-${t.turnier_id}`, data: t })),
    ...camps.map(c => ({ kind: 'camp', ts: sortTs(c.datum), key: `c-${c.event_id}`, data: c })),
  ].sort((a, b) => a.ts - b.ts);

  // Scroll-Reveal aktivieren, sobald die Daten (und damit die Karten) gerendert sind
  useScrollReveal([turniere, camps]);

  return (
    <div className="emp-page">
      <PublicNav />

      {/* SEO-H1 (visuell übernimmt die Sektion „Kommende Veranstaltungen") */}
      <h1 className="emp-visually-hidden">TDA Events — Turniere, Lehrgänge & Camps</h1>

      {/* ══ KOMMENDE VERANSTALTUNGEN ══ */}
      <section className="emp-events" id="veranstaltungen">
        <div className="emp-container">
          <div className="emp-events-header">
            <div className="emp-events-title-block reveal">
              <div className="emp-events-eyebrow">
                <span className="emp-events-eyebrow-dot" />
                TDA Veranstaltungen {new Date().getFullYear()}
              </div>
              <h2 className="emp-events-title">Kommende Veranstaltungen</h2>
              <p className="emp-events-sub">Turniere, Lehrgänge & Camps — klicke für alle Details und zur Anmeldung.</p>
            </div>
          </div>

          {loadingT ? (
            <div className="emp-events-loading">
              <div className="emp-spinner" />
              <span>Lädt Veranstaltungen…</span>
            </div>
          ) : turniere.length === 0 && camps.length === 0 ? (
            <div className="emp-events-empty">
              <div className="emp-events-empty-icon">📅</div>
              <p>Derzeit sind keine kommenden Veranstaltungen eingetragen.</p>
              <p className="emp-events-empty-sub">Schau bald wieder vorbei oder <a href="mailto:info@tda-intl.com" className="emp-link">kontaktiere uns</a>.</p>
            </div>
          ) : (
            <div className="emp-turnier-grid">
              {veranstaltungen.map((v, i) => (
                <div key={v.key} className={`reveal ${['', 'd1', 'd2'][i % 3]}`}>
                  {v.kind === 'turnier' ? (
                    <TurnierCard
                      turnier={v.data}
                      onClick={() => navigate(`/event/${v.data.turnier_id}`)}
                    />
                  ) : (
                    <CampCard
                      camp={v.data}
                      onClick={() => navigate(`/veranstaltung/${v.data.event_id}`)}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ══ VERANSTALTER-BAND (schlanker Verweis auf die Software-Seite) ══ */}
      <section className="emp-organizer-band">
        <div className="emp-container emp-organizer-inner reveal">
          <div className="emp-organizer-text">
            <div className="emp-organizer-eyebrow">🏟️ Für Turnierveranstalter</div>
            <h2 className="emp-organizer-title">Du willst selbst ein Turnier oder einen Lehrgang ausrichten?</h2>
            <p className="emp-organizer-sub">
              Die komplette TDA Turniersoftware — von der Online-Anmeldung über Live-Scoring bis zur Siegerehrung.
            </p>
          </div>
          <button className="emp-btn-primary emp-organizer-btn" onClick={() => navigate('/fuer-veranstalter')}>
            Software entdecken →
          </button>
        </div>
      </section>

      {/* RANKING HINWEIS */}
      <section className="emp-ranking-bar">
        <div className="emp-container emp-ranking-bar-inner reveal">
          <span className="emp-ranking-text">📈 Aktuelle Saison-Rangliste verfügbar</span>
          <button className="emp-btn-outline" onClick={() => navigate('/ranking')}>Rangliste ansehen →</button>
        </div>
      </section>

      {/* FOOTER */}
      {/* ══ NEWSLETTER ══ */}
      <section className="emp-newsletter">
        <div className="emp-container">
          <div className="emp-nl-inner reveal">
            <div className="emp-nl-text">
              <div className="emp-hero-eyebrow">Newsletter</div>
              <h2 className="emp-nl-title">Keine Veranstaltung verpassen</h2>
              <p className="emp-nl-sub">Erhalte Infos zu neuen Turnieren, Terminen und Neuigkeiten direkt per E-Mail.</p>
            </div>
            <form className="emp-nl-form" onSubmit={handleNlSubmit}>
              <input
                className="emp-nl-input"
                type="text"
                placeholder="Dein Name (optional)"
                value={nlName}
                onChange={e => setNlName(e.target.value)}
              />
              <input
                className="emp-nl-input"
                type="email"
                placeholder="deine@email.de"
                value={nlEmail}
                onChange={e => setNlEmail(e.target.value)}
                required
              />
              <button className="emp-nl-btn" type="submit" disabled={nlLoading}>
                {nlLoading ? '…' : 'Anmelden →'}
              </button>
              {nlMsg && <div className={`emp-nl-msg ${nlMsg.startsWith('✅') ? 'emp-nl-msg--ok' : 'emp-nl-msg--err'}`}>{nlMsg}</div>}
            </form>
          </div>
        </div>
      </section>

      <footer className="emp-footer">
        <div className="emp-container">
          <div className="emp-footer-links">
            <a href="https://tda-intl.org">TDA International</a>
            <a href="https://tda-intl.org/datenschutz">Datenschutz</a>
            <a href="https://tda-intl.org/impressum">Impressum</a>
            <a href="https://tda-intl.org/kontakt">Kontakt</a>
          </div>
          <p className="emp-footer-copy">© {new Date().getFullYear()} TDA International · events.tda-intl.org</p>
        </div>
      </footer>
    </div>
  );
}
